import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { debounce } from 'lodash-es';
import type { ColorName } from 'styles/config/colors';
import type { SizeKey } from 'styles/config/spacing';

import { safeResizeObserver } from '@polyai/common/utils/safeResizeObserver';

import { Stack } from 'components/atoms/Stack';
import Text from 'components/atoms/Text';
import Tooltip from 'components/atoms/Tooltip';
import { twMerge } from 'utils/twMerge';
import * as Styled from './Tabs.styled';

export interface TabConfig<ValueType extends string | number = string> {
  value: ValueType;
  label: string;
  disabled?: boolean;
  disabledTooltip?: string;
  icon?: React.ReactElement;
  iconPosition?: 'start' | 'end';
  iconInline?: boolean;
  iconSpacing?: SizeKey;
}

export interface TabsProps<ValueType extends string | number = string>
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'value' | 'onChange'> {
  value: ValueType;
  onChange: (value: ValueType) => void;
  tabs: TabConfig<ValueType>[];
  fullWidth?: boolean;
  TitleComponent?: React.ElementType;
  secondary?: boolean;
  color?: ColorName | 'transparent';
  withBorderBottom?: boolean;
}

interface IndicatorState {
  left?: number;
  width?: number;
}

const Tabs = <ValueType extends string | number = string>({
  className,
  value,
  onChange,
  tabs,
  fullWidth,
  TitleComponent,
  secondary,
  color,
  withBorderBottom = false,
  ...rest
}: TabsProps<ValueType>) => {
  const tabsRef = useRef<
    Partial<{ [key in ValueType]: HTMLButtonElement | null }>
  >({});
  const tabListRef = useRef<HTMLDivElement>(null);
  const [indicatorState, setIndicatorState] = useState<IndicatorState>({});

  const Title =
    TitleComponent ||
    (props => (
      <Text
        as="h3"
        {...props}
        className="flex items-center"
        variant="headerRegular"
      />
    ));

  const handleClick = (tab: TabConfig<ValueType>) => {
    if (tab.value !== value) {
      onChange(tab.value);
    }
  };

  const getTabMeta = useCallback(() => {
    const tabsNode = tabListRef.current;

    let tabsMeta;
    if (tabsNode) {
      const tabsRect = tabsNode.getBoundingClientRect();
      tabsMeta = {
        left: tabsRect.left,
        right: tabsRect.right,
        top: tabsRect.top,
        bottom: tabsRect.bottom,
      };
    }

    let tabMeta;
    if (tabsNode && value) {
      const tab = tabsRef.current[value];

      if (tab === undefined) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Invalid value for Tabs component');
        }
      } else if (!!tab) {
        const tabRect = tab.getBoundingClientRect();
        tabMeta = {
          left: tabRect.left,
          right: tabRect.right,
          top: tabRect.top,
          bottom: tabRect.bottom,
        };
      }
    }

    return { tabsMeta, tabMeta };
  }, [value]);

  const updateIndicatorState = useCallback(() => {
    const { tabsMeta, tabMeta } = getTabMeta();

    if (!tabsMeta || !tabMeta) {
      return;
    }

    setIndicatorState({
      left: tabMeta.left - tabsMeta.left,
      width: tabMeta.right - tabMeta.left,
    });
  }, [getTabMeta]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore
  useEffect(() => {
    if (tabListRef.current) {
      updateIndicatorState();
    }
  }, [updateIndicatorState, value, tabs]);

  useEffect(() => {
    const handleResize = debounce(() => {
      if (tabListRef.current) {
        updateIndicatorState();
      }
    }, 50);

    let resizeObserver: ResizeObserver;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = safeResizeObserver(handleResize);
      if (tabListRef.current) {
        Array.from(tabListRef.current.children).forEach(child => {
          resizeObserver.observe(child);
        });
      }
    }

    return () => {
      handleResize.cancel();
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [updateIndicatorState]);

  return (
    <div
      className={twMerge(
        clsx(
          {
            'border-b border-border-subtle': withBorderBottom,
          },
          className,
        ),
      )}
      {...rest}
    >
      <Stack ref={tabListRef} role="tablist">
        {tabs.map(tab => {
          return (
            <Tooltip key={tab.value} content={tab.disabledTooltip} asChild>
              <Styled.Tab
                ref={button => {
                  tabsRef.current[tab.value] = button;
                }}
                $fullWidth={fullWidth}
                $secondary={secondary}
                $selected={tab.value === value}
                $withBorderBottom={withBorderBottom}
                data-test-id={`tab-${tab.value}`}
                disabled={tab.disabled}
                role="tab"
                type="button"
                onClick={() => handleClick(tab)}
              >
                {tab.iconPosition === 'start' ? (
                  <Title>
                    {!!tab.icon && (
                      <Styled.TabIcon
                        $inline={tab.iconInline}
                        $spacing={tab.iconSpacing}
                      >
                        {tab.icon}
                      </Styled.TabIcon>
                    )}
                    {tab.label}
                  </Title>
                ) : (
                  <Title>
                    {tab.label}
                    {!!tab.icon && (
                      <Styled.TabIcon
                        $inline={tab.iconInline}
                        $spacing={tab.iconSpacing}
                      >
                        {tab.icon}
                      </Styled.TabIcon>
                    )}
                  </Title>
                )}
              </Styled.Tab>
            </Tooltip>
          );
        })}
      </Stack>
      {!withBorderBottom && (
        <div>
          <Styled.Indicator
            $color={color}
            $left={indicatorState.left}
            $width={indicatorState.width}
          />
        </div>
      )}
    </div>
  );
};

export default Tabs;
